import React, {ReactNode} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {NucleotideType} from 'biojs/BioConstants';
import {
  OFF_TARGET_SCORE_TYPE,
  DESIGN_TYPE,
  SORT_KEY,
  URLS,
  CRISPRGuide,
  OffTargetSite,
  CRISPRAnalysisSequenceAttributes,
  GuideGroupSort,
} from 'constants/CRISPRConstants';
import {CRISPRAnalysis} from 'constants/CRISPRConstants';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import BaseEditNewSequence from 'molbio/crispr/BaseEditNewSequence';
import * as BaseEditUtil from 'molbio/crispr/BaseEditUtil';
import CRISPRAnalysisStore from 'molbio/crispr/CRISPRAnalysisStore';
import * as CRISPRUtil from 'molbio/crispr/CRISPRUtil';
import GuideGroupTable, {
  GuideGroupTableColumnSpec,
  GuideGroupTableHelpOptions,
} from 'molbio/crispr/GuideGroupTable';
import OffTargetModal from 'molbio/crispr/OffTargetModal';
import * as sequtil from 'molbio/SeqUtil';

export function OffTargetScore({
  type,
  offTargetWarning,
}: {
  type: OFF_TARGET_SCORE_TYPE;
  offTargetWarning?: ReactNode;
}): JSX.Element {
  switch (type) {
    case OFF_TARGET_SCORE_TYPE.MIT: {
      return (
        <div>
          <a href={URLS.HSU_OFF_TARGET_PAPER} target="_blank" rel="noopener noreferrer">
            Specificity score
          </a>{' '}
          from Hsu et al. (2013).
          {offTargetWarning}
          <br />
          <br />
          Score is from 0-100. Higher is better.
        </div>
      );
    }
    case OFF_TARGET_SCORE_TYPE.CFD: {
      return (
        <div>
          Cutting frequency determination (CFD) score from
          <a href={URLS.CFD_SCORE_PAPER} target="_blank" rel="noopener noreferrer">
            Doench, Fusi et al. (2016).
          </a>
          <br />
          <br />
          Score is from 0-100. Higher is better.
        </div>
      );
    }
    default:
      throw new Error(`Unexpected off target score type: ${type}`);
  }
}

function BaseEditScore(): JSX.Element {
  return (
    <div>
      Base edit score is determined by the efficiency of BE1 activity in vitro using an NGG PAM
      <a
        href="http://www.nature.com/nature/journal/v533/n7603/full/nature17946.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        (Komor et al., Nature, 2016, Fig 2b)
      </a>
      .
    </div>
  );
}

export interface BaseEditGuideGroupTableProps {
  allowEdits: boolean;
  analysis: CRISPRAnalysis;
  guideGroups: Array<Array<CRISPRGuide>>;
  guideGroupSize: 1 | 2;
  sequence: CRISPRAnalysisSequenceAttributes;
  setSelection: (start: number, end: number | null) => void;
  guideGroupSort: GuideGroupSort;
  selectedGuideGroups: {[guideGroupKey: string]: boolean};
  openAssemblyTab: (analysis: unknown, guides: Array<CRISPRGuide>) => void;
  sequencePrimers: Array<{start: number; end: number; strand: StrandOfDoubleStranded}>;
  selection: {start: number; end: number | null};
  saveGuidePrimers: (folderId: string, nucleotideType: NucleotideType, tagSchemaId: string | null) => void;
  showGuidePrimers: (guides: Array<CRISPRGuide>) => void;
}

interface State {
  offTargetModalGuide: CRISPRGuide | null;
}

class BaseEditGuideGroupTable extends React.Component<BaseEditGuideGroupTableProps, State> {
  state: State = {
    offTargetModalGuide: null,
  };

  getColumnSpec = (): Array<GuideGroupTableColumnSpec> => {
    const offTargetHelp: GuideGroupTableHelpOptions = {
      title: 'Off-target score',
      content: <OffTargetScore type={OFF_TARGET_SCORE_TYPE.MIT} />,
    };
    const baseEditScoresHelp: GuideGroupTableHelpOptions = {
      title: 'Base-editing scores',
      content: <BaseEditScore />,
    };
    const guideStrandHelp: GuideGroupTableHelpOptions = {
      title: 'sgRNA sequence strand',
      content:
        'Denotes the strand the guide occurs on. Note that the guide is always displayed in terms of its ' +
        'forward strand bases for consistency with the translations displayed in the table.',
    };
    const newSequenceHelp: GuideGroupTableHelpOptions = {
      title: 'Base Edited DNA',
      content: `Red bases designate edited bases, and gray bases designate bases that fall outside of the
          protospacer region.`,
    };

    return [
      {
        sortKey: SORT_KEY.POSITION,
        header: 'Position',
      },
      {
        header: 'Guide strand',
        help: guideStrandHelp,
      },
      {
        header: 'Base edited DNA',
        help: newSequenceHelp,
      },
      {
        sortKey: SORT_KEY.NUM_BASE_EDITS,
        header: '# Edits',
      },
      {
        sortKey: SORT_KEY.BASE_EDIT_SCORES,
        header: 'Base-editing scores',
        help: baseEditScoresHelp,
      },
      {
        sortKey: SORT_KEY.SPECIFICITY_SINGLE,
        header: 'Off\u2011target',
        extraHeader: 'Score',
        help: offTargetHelp,
      },
    ];
  };

  getEditScores = (guide: CRISPRGuide): Array<number> =>
    BaseEditUtil.getForwardStrandEditScores(guide, this.props.sequence).filter((s: number) => s > 0);

  getNumEdits = (guide: CRISPRGuide): number => this.getEditScores(guide).length;

  getGuideGroupComponentsMap = (): {[key: string]: Array<Array<JSX.Element>>} => {
    const guideGroupsComponentMap: {[key: string]: Array<Array<JSX.Element>>} = {};
    this.props.guideGroups.forEach((guideGroup) => {
      const guide = guideGroup[0];
      guideGroupsComponentMap[CRISPRAnalysisStore.guideGroupKey(guideGroup)] = [
        [
          <td className="text-left" key="position">
            {sequtil.modelStartToDisplay(this.getGuidePosition(guide), this.props.sequence.startIndex)}
          </td>,
          <td className="mono text-left" key="strand">
            {CRISPRUtil.renderStrand(guide)}
          </td>,
          <td key="bases">
            <BaseEditNewSequence
              guide={guide}
              sequence={this.props.sequence}
              setSelection={this.props.setSelection}
            />
          </td>,
          <td className="text-left" key="numEdits">
            {this.getNumEdits(guide)}
          </td>,
          <td className="text-left" key="baseEditScores">
            {this.renderEditScores(guide)}
          </td>,
          <td className="text-left" key="offTargetScore">
            {this.renderOffTargetScore(guide)}
          </td>,
        ],
      ];
    });
    return guideGroupsComponentMap;
  };

  getGuidePosition = (guide: CRISPRGuide): number => CRISPRUtil.guideStart(guide, this.props.sequence.length);

  getMaxBaseEditScore = (guide: CRISPRGuide): number => _.max(this.getEditScores(guide));

  getSortIteratee =
    () =>
    (guideGroup: Array<CRISPRGuide>): number | null => {
      const guide = guideGroup[0];
      assert(guideGroup.length === 1, 'expected guide group to have length 1');
      const sortKey = this.props.guideGroupSort.key;
      switch (sortKey) {
        case SORT_KEY.POSITION:
          return this.getGuidePosition(guide);
        case SORT_KEY.NUM_BASE_EDITS:
          return this.getNumEdits(guide);
        case SORT_KEY.BASE_EDIT_SCORES:
          return this.getMaxBaseEditScore(guide);
        case SORT_KEY.SPECIFICITY_SINGLE:
          return this.offTargetScore(guide);
        default:
          return this.getGuidePosition(guide);
      }
    };

  getEditedForwardStrandBases = (guide: CRISPRGuide): string => {
    const forwardStrandBases = CRISPRUtil.getForwardStrandBases(guide);
    const editScores = BaseEditUtil.getForwardStrandEditScores(guide, this.props.sequence);
    return forwardStrandBases
      .split('')
      .map((base, i) => {
        if (editScores[i] > 0) {
          return BaseEditUtil.getForwardStrandEditedBase(base, guide.strand);
        }
        return base;
      })
      .join('');
  };

  guidesToTSV = (guides: Array<CRISPRGuide>): string => {
    const tsvHeaderRows = [
      ['Position', 'Guide', 'Guide Strand', "Edited 5'->3' Sequence", 'Edit Scores', 'Efficiency Score'].join(
        '\t'
      ),
    ];
    const tsvRows = guides.map((guide: CRISPRGuide) =>
      [
        sequtil.modelStartToDisplay(this.getGuidePosition(guide), this.props.sequence.startIndex),
        guide.bases,
        guide.strand,
        this.getEditedForwardStrandBases(guide),
        this.renderEditScores(guide),
        this.offTargetScore(guide),
      ].join('\t')
    );
    return tsvHeaderRows.concat(tsvRows).join('\n');
  };

  offTargetScore = (guide: CRISPRGuide): number | null => guide.offTargetScore;

  showOffTargetModal = (guide: CRISPRGuide): void => {
    this.setState({offTargetModalGuide: guide});
  };

  renderEditScores = (guide: CRISPRGuide): string =>
    this.getEditScores(guide)
      .map((score: number) => score.toFixed(1))
      .join(', ');

  renderOffTargetScore = (guide: CRISPRGuide): JSX.Element => {
    const score = this.offTargetScore(guide);
    const scoreString = score ? score.toFixed(1) : null;
    return (
      <a
        onClick={() => {
          this.showOffTargetModal(guide);
        }}
      >
        {scoreString}
      </a>
    );
  };

  render(): JSX.Element {
    assert(
      this.props.analysis.designType === DESIGN_TYPE.BASE_EDIT,
      'Cannot render BaseEditGuideGroupTable with non base edit design type'
    );
    const guideGroupTableProps = {
      ...this.props,
      guideGroupComponentsMap: this.getGuideGroupComponentsMap(),
      columns: this.getColumnSpec(),
      tableClassName: 'crispr-guidesTable',
      getGuidePosition: this.getGuidePosition,
      getSortIteratee: this.getSortIteratee,
      guidesToTSV: this.guidesToTSV,
    };
    const hasInvalidOverlappingTranslations = this.props.guideGroups.some((guideGroup) =>
      BaseEditUtil.hasInvalidOverlappingTranslations(guideGroup[0], this.props.sequence)
    );

    return (
      <div>
        {(() => {
          if (hasInvalidOverlappingTranslations) {
            return (
              <div className="alert alert-warning">
                Guides underlined below overlap with unsupported translations and have been displayed without
                any translations. Unsupported cases include:
                <br />
                (a) Translations on the reverse strand
                <br />
                (b) Overlapping translations
                <br />
                (c) Guides which effect multiple translations
                <br />
                (d) Translations crossing the origin of a circular sequence
              </div>
            );
          }
          return null;
        })()}
        {(() => {
          if (this.state.offTargetModalGuide) {
            return (
              <OffTargetModal
                analysis={this.props.analysis}
                // Note that some CRISPR components expect `sites` to be non-null. We should probably have
                // versions of `CRISPRGuide` with both stricter and looser types to be more accurate. Currently
                // it's unclear to me exactly at what point the type can be constrained.
                guide={this.state.offTargetModalGuide as CRISPRGuide & {sites: Array<OffTargetSite>}}
                onRequestClose={() => {
                  this.setState({offTargetModalGuide: null});
                }}
                region={
                  _.findWhere(this.props.analysis.regions, {
                    id: this.state.offTargetModalGuide.regionId,
                  })!
                }
              />
            );
          }
          return null;
        })()}
        <GuideGroupTable {...guideGroupTableProps} />
      </div>
    );
  }
}

export default BaseEditGuideGroupTable;
